import Link from 'next/link';
import styled from 'styled-components';
import {
  logAnalyticsEvent,
  EventType,
  captureEventTrigger,
} from '@pass-frontend/common/analytics';
import { ContentWidthWrapper } from '@pass-frontend/common/components/content-width-wrapper/ui';
import { STANDALONE_PATHS } from '@pass-frontend/common/constants/paths';
import { useIntl } from '@pass-frontend/common/i18n';
import {
  FooterDataProps,
  FooterItems,
} from '@pass-frontend/features/standalone/data-access';
import { MEDIA_QUERY } from '@pass-frontend/utility/style-helpers';

const FooterWrapper = styled.footer`
  padding: 16px 0;

  .content-wrapper {
    flex-direction: column;

    ${MEDIA_QUERY.desktop} {
      align-items: center;
      flex-direction: row;
    }
  }

  .copyright {
    line-height: 24px;
    margin-bottom: 8px;

    ${MEDIA_QUERY.desktop} {
      padding-right: 24px;
      border-right: 2px solid #b1b2b4;
      margin-bottom: 0;
    }
  }

  .link {
    line-height: 24px;
  }

  ${MEDIA_QUERY.desktop} {
    padding: 26px 0;
  }
`;

const StyledLinkWrapper = styled.div`
  padding: 0 8px;
  border-right: 1px solid #b1b2b4;

  &:first-child {
    padding-left: 0;
  }
  &:last-child {
    padding-right: 0;
    border: none;
  }

  ${MEDIA_QUERY.desktop} {
    padding: 0 24px;
    border-right-width: 2px;

    &:first-child {
      padding-left: 24px !important;
    }
  }
`;

export function Footer(props: FooterDataProps) {
  const { t } = useIntl();

  const handleClick = (event: any, url?: string) => {
    if (url === STANDALONE_PATHS.FAQS) {
      captureEventTrigger(event);
    } else {
      logAnalyticsEvent(
        EventType.ExternalNav,
        {
          section: { pageName: url },
        },
        event.nativeEvent,
      );
    }
  };

  const renderFooterItem = (params: FooterItems) => {
    const target = params.openInSameTab ? '_self' : '_blank';
    const rel = params.openInSameTab ? undefined : 'noreferrer';

    switch (params.type) {
      case 'link': {
        const commonAttributes = {
          className: 'is-body-3-regular has-text-primary link',
          'data-ui-element-name': params.dataUIElementName,
          onClick: (e: any) => handleClick(e, params.redirectURL),
        };

        if (params.isInternalLink) {
          return (
            <Link href={params.redirectURL || ''} {...commonAttributes}>
              {t(params.labelKey)}
            </Link>
          );
        }

        return (
          <a
            {...commonAttributes}
            href={params.redirectURL}
            target={target}
            rel={rel}
          >
            {t(params.labelKey)}
          </a>
        );
      }
      default:
        return <p>{t(params.labelKey)}</p>;
    }
  };
  return (
    <FooterWrapper>
      <ContentWidthWrapper>
        <div className="content-wrapper is-display-flex">
          <p className="is-body-3-regular copyright">{t(props.copyRightKey)}</p>
          <div className="is-display-flex is-align-items-center is-flex-wrap-wrap">
            {props.items.map((props, i) => (
              <StyledLinkWrapper key={i}>
                {renderFooterItem(props)}
              </StyledLinkWrapper>
            ))}
          </div>
        </div>
      </ContentWidthWrapper>
    </FooterWrapper>
  );
}

export default Footer;
